import { HintTooltip } from '../../../../components/hint-tooltip';
import { ProductNameWithLabels } from '../../../../components/product-table/columns/product-name-with-labels-column';
import { CollapsingProductLabels } from '../../../../components/products/collapsing-product-labels';
import { remapColumn, type StrictColumnDef } from '../../../../components/tanstack-table/column';
import type { Product } from '../../../../data/product';
import type { ProductOrderItem } from '../data/product-order-item';
import { toProduct } from '../utils';

type PivotColumnOptions = {
  onPivot: (product: Product) => void;
  isEligible: (product: Product) => boolean;
  tooltipText: (product: Product) => string;
  shortcutHint?: string;
  // 'underline' (default): dotted underline always visible — the affordance
  // is signal because few products qualify (order view).
  // 'hover-only': dotted underline appears on hover/focus only — used when
  // every row would otherwise be underlined (buyup view), so the at-rest
  // table stays calm.
  affordance?: 'underline' | 'hover-only';
};

const STATIC_UNDERLINE =
  'underline decoration-dotted decoration-2 decoration-primary-600 underline-offset-2';
const HOVER_UNDERLINE =
  'decoration-dotted decoration-2 decoration-primary-600 underline-offset-2 hover:underline focus-visible:underline';

/**
 * Pivot-aware variant of the Product column. Cells whose product passes
 * `isEligible` render a clickable button with a dotted underline that calls
 * `onPivot`; everything else falls through to the base Product cell. Used
 * from the regular order view (jumps to buyup view) and from the buyup
 * view itself (filters search to the clicked product's brand).
 */
export function makeProductPivotColumn({
  onPivot,
  isEligible,
  tooltipText,
  shortcutHint,
  affordance = 'underline',
}: PivotColumnOptions): StrictColumnDef<ProductOrderItem> {
  const base = remapColumn(ProductNameWithLabels, toProduct);
  const underlineClass = affordance === 'hover-only' ? HOVER_UNDERLINE : STATIC_UNDERLINE;

  return {
    ...base,
    cell: (context) => {
      const product = context.row.original.product;
      if (!isEligible(product)) {
        return base.cell?.(context);
      }

      return (
        <div className="flex items-center w-full relative overflow-hidden">
          <HintTooltip text={tooltipText(product)} shortcut={shortcutHint}>
            <button
              type="button"
              className={`shrink-0 max-w-full truncate text-left cursor-pointer text-gray-900 hover:text-gray-900 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary-600 rounded-sm ${underlineClass}`}
              onClick={(event) => {
                event.stopPropagation();
                onPivot(product);
              }}
            >
              {product.brand}
            </button>
          </HintTooltip>
          <span className="min-w-0 flex-1 truncate text-gray-900">{` | ${product.description}`}</span>
          <CollapsingProductLabels labelIds={product.label_ids} />
        </div>
      );
    },
  };
}
